import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { scoutActivity } from "@/lib/scoutApi";

const PREF_KEY = "atfq_scout_notify";
const SINCE_KEY = "atfq_scout_notify_since";
const POLL_MS = 60000;
const ICON = "/icons/scout-192.png";

const supported = () => typeof window !== "undefined" && "Notification" in window;

const notify = (title, body, tag, onClick) => {
  const n = new Notification(title, { body, icon: ICON, badge: ICON, tag });
  n.onclick = () => {
    window.focus();
    onClick?.();
    n.close();
  };
};

const announce = ({ questions, runs }, goInbox) => {
  if (questions.length === 1) {
    const q = questions[0];
    notify(
      q.guest ? `New question for ${q.guest}` : "New listener question",
      `${q.submitter_name || "Anonymous"}: ${q.question.slice(0, 120)}`,
      `q-${q.id}`,
      goInbox
    );
  } else if (questions.length > 1) {
    notify(`${questions.length} new listener questions`, questions.map((q) => `· ${q.question.slice(0, 60)}`).join("\n"), "q-batch", goInbox);
  }
  if (runs.length > 1) {
    notify(`${runs.length} scout runs finished`, `${runs.reduce((n, r) => n + (r.candidate_count || 0), 0)} new candidates landed in the CRM.`, "run-batch");
    return;
  }
  runs.forEach((r) =>
    notify(
      r.status === "done" ? `Scout run finished · ${r.candidate_count} found` : "Scout run failed",
      r.status === "done" ? `${r.triggered_by === "scheduled" ? "Daily" : "Manual"} run landed new candidates in the CRM.` : r.error || "Check the dashboard.",
      `run-${r.id}`
    )
  );
};

export const ScoutNotifications = ({ onOpenInbox }) => {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(PREF_KEY) === "1");
  const [permission, setPermission] = useState(() => (supported() ? Notification.permission : "unsupported"));
  const sinceRef = useRef(localStorage.getItem(SINCE_KEY) || new Date().toISOString());

  const active = enabled && permission === "granted";

  useEffect(() => {
    if (!active) return undefined;
    let stopped = false;
    const tick = async () => {
      try {
        const data = await scoutActivity(sinceRef.current);
        if (stopped) return;
        sinceRef.current = data.now;
        localStorage.setItem(SINCE_KEY, data.now);
        announce(data, onOpenInbox);
      } catch (err) {
        console.warn("Scout activity poll failed; retrying", err?.message || err);
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [active, onOpenInbox]);

  const toggle = async () => {
    if (enabled) {
      setEnabled(false);
      localStorage.setItem(PREF_KEY, "0");
      toast("Desktop notifications off");
      return;
    }
    if (!supported()) return toast.error("This browser doesn't support desktop notifications.");
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p !== "granted") return toast.error("Notifications are blocked — allow them for this site in your browser settings.");
    setEnabled(true);
    localStorage.setItem(PREF_KEY, "1");
    sinceRef.current = new Date().toISOString();
    localStorage.setItem(SINCE_KEY, sinceRef.current);
    notify("ATFQ Scout notifications on", "You'll hear about new listener questions and finished scout runs while Scout is open.", "hello");
  };

  const Icon = active ? BellRing : permission === "denied" ? BellOff : Bell;
  const label = active ? "Notifications on" : permission === "denied" ? "Notifications blocked" : "Notify me";

  return (
    <button
      type="button"
      data-testid="scout-notify-toggle"
      aria-pressed={active}
      onClick={toggle}
      title="Desktop notifications for new listener questions and finished scout runs (while Scout is open)"
      className={`inline-flex items-center gap-2 text-sm ${active ? "text-[var(--accent)]" : "text-slate-500 hover:text-[#0A192F]"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
};

export default ScoutNotifications;
